<template>
  <div class="tw-benchmark-table">
    <div v-if="error" class="message message-error">
      データ取得に失敗しました。恐れ入りますが、時間を置いて再度お試しください。
    </div>

    <div v-else-if="!loading && posts.length === 0" class="message message-info">
      キャンペーン投稿のデータがありません。
    </div>
    <template v-else>
      <div
        v-show="hasHorizontalOverflow"
        ref="topScrollbar"
        class="tw-benchmark-table__top-scrollbar"
        @scroll="onTopScroll"
      >
        <div class="tw-benchmark-table__top-scrollbar-spacer" :style="{ width: `${scrollWidth}px` }" />
      </div>
      <el-table
        ref="table"
        v-loading="loading"
        :data="posts"
        style="width: 100%"
        :height="tableHeight"
        @row-click="onRowClick"
      >
        <el-table-column :key="selectAllHeaderKey" width="48" fixed="left" align="center">
          <template slot="header">
            <el-checkbox
              :value="isAllSelected"
              :indeterminate="isIndeterminate"
              @click.native.prevent.stop="$emit('toggle-select-all')"
            />
          </template>
          <template slot-scope="scope">
            <el-checkbox
              :value="selectedPostIds.includes(scope.row.post.id)"
              @click.native.prevent.stop="$emit('toggle-select', scope.row.post.id)"
            />
          </template>
        </el-table-column>

        <el-table-column label="投稿プレビュー" fixed="left" :min-width="previewEnabled ? 400 : 200">
          <template slot-scope="scope">
            <TwBenchmarkPostPreviewCell
              :row="scope.row"
              :preview-enabled="previewEnabled"
              @assign-tag="$emit('assign-tag', $event)"
            />
          </template>
        </el-table-column>

        <el-table-column
          v-for="(column, index) in orderedColumns"
          :key="`${index}-${column.key}`"
          :min-width="column.width"
          :render-header="h => renderColumnHeader(h, column)"
        >
          <template slot-scope="scope">
            <TwBenchmarkTableBody :column-key="column.key" :row="scope.row" />
          </template>
        </el-table-column>
      </el-table>
    </template>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import {
  computed,
  CreateElement,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  VNode,
  watch
} from 'vue'

import Util from '@/client/core/util'
import { TrackingService } from '@/client/services'
import { type PostListResponse, type SortKey } from '@/typings/twitter_benchmark_campaign_posts'

import { useTableSort } from '../../hooks/useTableSort'
import { useTwBenchmarkPostsStore } from '../../store/useTwBenchmarkPostsStore'
import { useTwBenchmarkTableSettingsStore } from '../../store/useTwBenchmarkTableSettingsStore'
import { DEFAULT_TABLE_COLUMNS_KEY, type TwBenchmarkDefaultTableColums } from '../../utils/columns'
import TwBenchmarkPostPreviewCell from './components/TwBenchmarkPostPreviewCell.vue'
import TwBenchmarkTableBody from './components/TwBenchmarkTableBody.vue'

type PostRow = PostListResponse['posts'][number]
type SortOrder = 'asc' | 'desc' | null

/**
 * X 競合投稿一覧テーブル。
 */
export default defineComponent({
  name: 'TwBenchmarkTable',

  components: {
    TwBenchmarkTableBody,
    TwBenchmarkPostPreviewCell
  },

  props: {
    selectedPostIds: {
      type: Array as PropType<number[]>,
      default: () => []
    }
  },

  emits: ['row-click', 'toggle-select', 'toggle-select-all', 'assign-tag'],

  setup(props, { emit }) {
    const postsStore = useTwBenchmarkPostsStore()
    const settingsStore = useTwBenchmarkTableSettingsStore()
    const { changeSort, sortOrderOf } = useTableSort()

    const orderedColumns = computed(() =>
      settingsStore.columnOrder
        .filter(key => settingsStore.visibleColumns[key])
        .map(key => DEFAULT_TABLE_COLUMNS_KEY.get(key))
        .filter((col): col is NonNullable<typeof col> => col !== undefined)
    )

    const isAllSelected = computed(
      () => postsStore.posts.length > 0 && postsStore.posts.every(row => props.selectedPostIds.includes(row.post.id))
    )

    const isIndeterminate = computed(() => {
      const selectedCount = postsStore.posts.filter(row => props.selectedPostIds.includes(row.post.id)).length
      return selectedCount > 0 && selectedCount < postsStore.posts.length
    })

    const selectAllHeaderKey = computed(() => `select-all-${isAllSelected.value}-${isIndeterminate.value}`)

    const resolveSortOrder = (sortKey: SortKey | undefined): SortOrder => (sortKey ? sortOrderOf(sortKey) : null)

    const onSort = (sortKey: SortKey | undefined, label: string): void => {
      if (sortKey) {
        TrackingService.sendEvent(`sort:競合キャンペーン分析|${label}`)
        changeSort(sortKey)
      }
    }

    const toSortByString = (col: TwBenchmarkDefaultTableColums): string => {
      if (!col.sortKey) return ''
      const order = resolveSortOrder(col.sortKey)
      if (!order) return ''
      return order === 'desc' ? `-${col.sortKey}` : col.sortKey
    }

    const renderColumnHeader = (h: CreateElement, col: TwBenchmarkDefaultTableColums): VNode => {
      const children: VNode[] = [h('span', { class: 'tw-benchmark-column-header__label' }, col.label)]

      if (col.tooltip) {
        children.push(
          h('el-tooltip', { props: { content: col.tooltip, placement: 'top', enterable: false } }, [
            h('i', {
              class: 'mdi mdi-help-circle',
              on: { click: (e: Event) => e.stopPropagation() }
            })
          ])
        )
      }

      if (col.sortable) {
        children.push(
          h('span', { class: 'caret-wrapper' }, [
            h('i', { class: 'sort-caret ascending' }),
            h('i', { class: 'sort-caret descending' })
          ])
        )
      }

      const sortStateClass = col.sortable
        ? `sortable ${Util.getSortClass(toSortByString(col), col.sortKey ?? '')}`.trim()
        : ''

      return h(
        'div',
        {
          class: [
            'tw-benchmark-column-header',
            { 'tw-benchmark-column-header--sortable': col.sortable },
            sortStateClass
          ],
          on: {
            click: () => onSort(col.sortKey, col.label)
          }
        },
        children
      )
    }

    const onRowClick = (row: PostRow): void => {
      emit('row-click', row.post.id)
    }

    const table = ref<{ $el: HTMLElement } | null>(null)
    const topScrollbar = ref<HTMLElement | null>(null)
    const scrollWidth = ref(0)
    const hasHorizontalOverflow = ref(false)

    const TABLE_BOTTOM_RESERVED_HEIGHT = 96
    const TABLE_MIN_HEIGHT = 240
    const tableHeight = ref(TABLE_MIN_HEIGHT)

    const updateTableHeight = (): void => {
      const el = table.value?.$el
      if (!el) return
      const top = el.getBoundingClientRect().top
      const available = window.innerHeight - top - TABLE_BOTTOM_RESERVED_HEIGHT
      tableHeight.value = Math.max(available, TABLE_MIN_HEIGHT)
    }

    const getBodyWrapper = (): HTMLElement | null =>
      table.value?.$el.querySelector<HTMLElement>('.el-table__body-wrapper') ?? null

    const syncScrollWidth = (): void => {
      const bodyWrapper = getBodyWrapper()
      if (!bodyWrapper) return
      scrollWidth.value = bodyWrapper.scrollWidth
      hasHorizontalOverflow.value = bodyWrapper.scrollWidth > bodyWrapper.clientWidth
    }

    let isSyncing = false

    const onTopScroll = (): void => {
      if (isSyncing) return
      const bodyWrapper = getBodyWrapper()
      if (!bodyWrapper || !topScrollbar.value) return
      isSyncing = true
      bodyWrapper.scrollLeft = topScrollbar.value.scrollLeft
      isSyncing = false
    }

    const onBodyScroll = (): void => {
      if (isSyncing) return
      const bodyWrapper = getBodyWrapper()
      if (!bodyWrapper || !topScrollbar.value) return
      isSyncing = true
      topScrollbar.value.scrollLeft = bodyWrapper.scrollLeft
      isSyncing = false
    }

    const resizeObserver = new ResizeObserver(() => syncScrollWidth())

    onMounted(() => {
      const bodyWrapper = getBodyWrapper()
      if (!bodyWrapper) return
      bodyWrapper.addEventListener('scroll', onBodyScroll)
      resizeObserver.observe(bodyWrapper)
      const innerTable = bodyWrapper.querySelector<HTMLElement>('.el-table__body')
      if (innerTable) resizeObserver.observe(innerTable)
      syncScrollWidth()

      updateTableHeight()
      window.addEventListener('resize', updateTableHeight)
    })

    onBeforeUnmount(() => {
      const bodyWrapper = getBodyWrapper()
      bodyWrapper?.removeEventListener('scroll', onBodyScroll)
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateTableHeight)
    })

    watch([orderedColumns, () => settingsStore.previewEnabled, () => postsStore.posts.length], () => {
      void nextTick(syncScrollWidth)
    })

    watch(
      () => [postsStore.loading, postsStore.error, postsStore.posts.length === 0],
      () => {
        void nextTick(updateTableHeight)
      }
    )

    watch(
      () => postsStore.currentPage,
      () => {
        const bodyWrapper = getBodyWrapper()
        if (bodyWrapper) bodyWrapper.scrollTop = 0
      }
    )

    return {
      posts: computed(() => postsStore.posts),
      loading: computed(() => postsStore.loading),
      error: computed(() => postsStore.error),
      previewEnabled: computed(() => settingsStore.previewEnabled),
      orderedColumns,
      isAllSelected,
      isIndeterminate,
      selectAllHeaderKey,
      renderColumnHeader,
      onRowClick,
      table,
      topScrollbar,
      scrollWidth,
      hasHorizontalOverflow,
      onTopScroll,
      tableHeight
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.tw-benchmark-table {
  :deep(.el-table__header-wrapper .el-table__header th.el-table__cell) {
    border-bottom: 1px solid $border-color;
  }

  &__top-scrollbar {
    position: sticky;
    top: 0;
    z-index: 1;
    overflow: auto hidden;
  }

  &__top-scrollbar-spacer {
    height: 1px;
  }

  :deep(.tw-benchmark-column-header) {
    display: flex;
    gap: $spacing-unit * 0.5;
    align-items: center;
    justify-content: flex-start;

    .mdi-help-circle {
      margin: 0;
      font-size: 20px;
      line-height: 1;
      color: $sub-text-color;
      cursor: help;
    }
  }

  :deep(.tw-benchmark-column-header--sortable) {
    cursor: pointer;
  }
}
</style>
